<template>
  <div class="form-section">
    <div class="input-group">
      <div class="avatar-upload-container">
        <div class="avatar-upload-area">
          <div
            v-if="!selectedAvatar"
            class="avatar-placeholder"
            @click="showModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="32"
              class="avatar-plus-icon"
            />
            <span class="avatar-placeholder-text">Choose Avatar</span>
          </div>
          <div
            v-else
            class="avatar-preview"
            @click="showModal"
          >
            <img
              :src="selectedAvatar.avatarThumbnail"
              :alt="selectedAvatar.avatarName"
              class="avatar-image"
            >
            <div class="avatar-overlay">
              <feather-icon
                icon="EditIcon"
                size="16"
                class="avatar-edit-icon"
              />
            </div>
            <button
              class="avatar-remove-btn"
              :title="`Remove ${selectedAvatar.avatarName}`"
              @click.stop="removeAvatar"
            >
              <feather-icon
                icon="XIcon"
                size="14"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Avatar Selection Modal -->
    <b-modal
      v-model="showAvatarSelectionModal"
      id="avatar-selection-modal"
      size="lg"
      hide-footer
      centered
      title="Choose Avatar"
      @show="loadAvatars"
      @hidden="closeModal"
    >
      <div class="avatar-modal-content">
        <p class="modal-description">
          Select an avatar to use for generating the Avatar Product video.
        </p>

        <!-- Loading State - Skeleton -->
        <div
          v-if="loading"
          class="avatar-options-grid skeleton-grid"
        >
          <div
            v-for="n in 30"
            :key="`skeleton-${n}`"
            class="avatar-option skeleton-item"
          >
            <div class="skeleton-image" />
            <div class="skeleton-text skeleton-name" />
            <div class="skeleton-text skeleton-name-short" />
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="error-container"
        >
          <div class="error-icon">
            <feather-icon
              icon="AlertCircleIcon"
              size="48"
            />
          </div>
          <p class="error-text">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            @click="loadAvatars"
          >
            Try Again
          </b-button>
        </div>

        <!-- Avatar Grid -->
        <div
          v-else
          class="avatar-options-grid"
          @scroll="handleScroll"
        >
          <VideoCard
            v-for="avatar in avatars"
            :key="avatar._id"
            :video-id="avatar._id"
            :video-url="avatar.avatarPreviewUrl"
            :thumbnail-url="avatar.avatarThumbnail"
            :title="avatar.avatarName"
            :is-selected="tempSelectedAvatar && tempSelectedAvatar._id === avatar._id"
            :is-playing="getCardState(avatar._id, 'playing')"
            :show-title="true"
            selection-type="radio"
            @select="handleAvatarSelect"
            @update:is-playing="handleAvatarPlayingUpdate"
          />
        </div>

        <!-- Load More Indicator -->
        <div
          v-if="!loading && !error && hasMore"
          class="load-more-container"
        >
          <div
            v-if="loadingMore"
            class="loading-more"
          >
            <div class="spinner-border spinner-border-sm text-primary" />
            <span class="loading-text">Loading more avatars...</span>
          </div>
          <div
            v-else
            class="load-more-info"
          >
            Showing {{ avatars.length }} of {{ total }} avatars
          </div>
        </div>

        <!-- No More Data -->
        <div
          v-if="!loading && !error && !hasMore && avatars.length > 0"
          class="no-more-data"
        >
          <div class="no-more-text">
            All {{ total }} avatars loaded
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="closeModal"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="primary-gradient-btn"
          @click="confirmSelection"
        >
          Select
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton } from 'bootstrap-vue'
import avatarService from '@/services/avatarService'
import VideoCard from './VideoCard.vue'

export default {
  name: 'VideoFormAvatar',
  components: {
    BModal,
    BButton,
    VideoCard,
  },
  props: {
    selectedAvatar: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showAvatarSelectionModal: false,
      avatars: [],
      loading: false,
      loadingMore: false,
      error: null,
      currentPage: 1,
      limit: 30,
      total: 0,
      hasMore: true,
      tempSelectedAvatar: null,
      cardStates: {},
    }
  },
  watch: {
    avatars() {
      this.$nextTick(() => {
        this.ensureCardStates()
      })
    },
  },
  methods: {
    async showModal() {
      this.tempSelectedAvatar = this.selectedAvatar
      this.showAvatarSelectionModal = true
      if (this.avatars.length === 0) {
        await this.loadAvatars()
      }
    },

    closeModal() {
      this.tempSelectedAvatar = null
      this.showAvatarSelectionModal = false
    },

    async loadAvatars(page = 1, append = false) {
      // Ensure page is always a valid number
      const pageNumber = Number(page) || 1

      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.avatars = []
        this.currentPage = 1
        this.hasMore = true
      }
      this.error = null

      try {
        const response = await avatarService.getAvatarList({
          page: pageNumber,
          limit: this.limit,
        })

        const { data } = response

        // Handle different possible response structures
        let newAvatars = []
        if (data && data.result && data.result.content) {
          newAvatars = data.result.content
        } else if (data && data.content) {
          newAvatars = data.content
        } else if (data && Array.isArray(data)) {
          newAvatars = data
        } else if (data && data.data && Array.isArray(data.data)) {
          newAvatars = data.data
        }

        if (append) {
          this.avatars = [...this.avatars, ...newAvatars]
        } else {
          this.avatars = newAvatars
        }

        // Handle pagination data from different response structures
        const paginationData = data.result || data
        this.currentPage = Number(paginationData.page || paginationData.currentPage || 1)
        this.total = Number(paginationData.total || paginationData.totalCount || newAvatars.length)
        this.hasMore = this.avatars.length < this.total
      } catch {
        this.error = 'Failed to load avatars. Please try again.'
        this.$toast.error('Failed to load avatars')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async loadMore() {
      if (this.hasMore && !this.loadingMore) {
        const nextPage = Number(this.currentPage) + 1
        await this.loadAvatars(nextPage, true)
      }
    },

    handleScroll(event) {
      const { target } = event
      const { scrollTop, scrollHeight, clientHeight } = target

      // Load more when user scrolls to bottom (with 100px buffer)
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

      if (isNearBottom && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    ensureCardStates() {
      this.avatars.forEach(avatar => {
        this.ensureCardState(avatar)
      })
    },

    ensureCardState(avatar) {
      if (!avatar?._id) return // eslint-disable-line no-underscore-dangle
      const existing = this.cardStates[avatar._id] || {} // eslint-disable-line no-underscore-dangle
      this.$set(this.cardStates, avatar._id, { // eslint-disable-line no-underscore-dangle
        playing: existing.playing ?? false,
      })
    },

    updateCardState(id, updates) {
      if (!id) return
      const prev = this.cardStates[id] || {}
      this.$set(this.cardStates, id, { ...prev, ...updates })
    },

    getCardState(id, key) {
      const state = this.cardStates[id]
      if (!state) {
        return key === 'playing' ? false : undefined
      }
      if (key === 'playing') return state.playing === true
      return state[key]
    },

    handleAvatarSelect(avatarId) {
      const avatar = this.avatars.find(a => a._id === avatarId) // eslint-disable-line no-underscore-dangle
      if (avatar) {
        this.tempSelectedAvatar = avatar
      }
    },

    handleAvatarPlayingUpdate(avatarId, isPlaying) {
      this.updateCardState(avatarId, { playing: isPlaying })
    },

    confirmSelection() {
      this.$emit('avatar-selected', this.tempSelectedAvatar)
      this.tempSelectedAvatar = null
      this.showAvatarSelectionModal = false
    },

    removeAvatar() {
      this.$emit('avatar-removed')
    },
  },
}
</script>

<style lang="scss" scoped>
.avatar-upload-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.avatar-upload-area {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  min-height: 120px;
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  border: 2px dashed #6c757d;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #007bff;
    background: #f8f9ff;
  }

  .avatar-plus-icon {
    color: #6c757d;
    margin-bottom: 8px;
  }

  .avatar-placeholder-text {
    font-size: 14px;
    color: #6c757d;
    font-weight: 500;
  }
}

.avatar-preview {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;

    .avatar-edit-icon {
      color: white;
    }
  }

  &:hover .avatar-overlay {
    opacity: 1;
  }

  .avatar-remove-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(220, 53, 69, 0.3);
    border-radius: 50%;
    color: #dc3545;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    opacity: 0;

    &:hover {
      background: #dc3545;
      color: white;
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  &:hover .avatar-remove-btn {
    opacity: 1;
  }
}

.avatar-modal-content {
  .modal-description {
    color: #666;
    font-size: 14px;
  }

  .avatar-options-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    max-height: 500px;
    overflow-y: auto;
    padding: 8px 8px 0 0;
    scroll-behavior: smooth;
  }

}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .spinner-border {
    margin-bottom: 16px;
  }

  .loading-text {
    color: #666;
    font-size: 14px;
    margin: 0;
  }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;

  .error-icon {
    color: #dc3545;
    margin-bottom: 16px;
  }

  .error-text {
    color: #666;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
  }
}

.load-more-container {
  margin-top: 15px;
  padding: 0 16px;
  text-align: right;

  .loading-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    color: #666;
    font-size: 14px;

    .loading-text {
      margin: 0;
    }
  }

  .load-more-info {
    color: #666;
    font-size: 14px;
    font-style: italic;
  }
}

.no-more-data {
  margin-top: 20px;
  padding: 16px;
  text-align: center;

  .no-more-text {
    color: #28a745;
    font-size: 14px;
    font-weight: 500;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;

  .btn {
    border-radius: 20px !important;
    padding: 8px 16px;
    font-weight: 600;
  }

  .btn-outline-secondary {
    border: 1px solid #6c757d !important;
    color: #6c757d !important;
    background: transparent !important;

    &:hover,
    &:focus,
    &:active {
      background: #6c757d !important;
      color: #ffffff !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
    }
  }

  .primary-gradient-btn {
    background: linear-gradient(135deg, #8ec5fc 0%, #a855f7 100%) !important;
    border: none !important;
    color: #ffffff !important;

    &:hover,
    &:focus,
    &:active {
      background: linear-gradient(135deg, #7bb3f9 0%, #9333ea 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 4px 12px rgba(142, 197, 252, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      background: #e5e7eb !important;
      color: #9ca3af !important;
      transform: none;
      box-shadow: none;
    }
  }
}

/* Skeleton Loading Styles */
.skeleton-grid {
  .skeleton-item {
    position: relative;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    cursor: default;
    pointer-events: none;

    .skeleton-image {
      width: 100%;
      height: 120px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 6px;
      margin-bottom: 8px;
    }

    .skeleton-text {
      height: 12px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 4px;

      &.skeleton-name {
        width: 80%;
      }

      &.skeleton-name-short {
        width: 60%;
        margin-bottom: 0;
      }
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
